/**
 * Shared Server-Sent Events (SSE) streaming utilities.
 *
 * Parses event: / data: lines from a fetch Response body so callers don't
 * have to duplicate TextDecoder / getReader logic across the codebase.
 */

export interface SSEEvent<T = unknown> {
  event: string;
  data: T;
}

/**
 * Read an SSE stream from a fetch Response and yield parsed events.
 *
 * - Handles `event:` lines to name the following `data:` events.
 * - Parses `data:` payloads as JSON when possible; otherwise yields the raw string.
 * - Ignores `[DONE]` sentinel lines (common in OpenAI-style streams).
 */
export async function* readSSEEvents(
  response: Response
): AsyncGenerator<SSEEvent, void, unknown> {
  if (!response.body) return;

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let currentEvent = 'message';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';

      for (const line of lines) {
        if (line.startsWith('event: ')) {
          currentEvent = line.slice(7).trim();
          continue;
        }

        if (line.startsWith('data: ')) {
          const payload = line.slice(6).trim();
          if (payload === '[DONE]') continue;

          let data: unknown = payload;
          try {
            data = JSON.parse(payload);
          } catch {
            // keep raw string
          }

          yield { event: currentEvent, data };
          currentEvent = 'message';
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}

/**
 * Higher-level helper for OpenAI-style streams where each `data:` payload
 * contains a `{ text?: string, error?: string, done?: boolean, meta?: object }` object.
 *
 * Calls `onChunk` for every text delta, `onMeta` for metadata envelopes, throws on `error`, and stops on `done`.
 */
export async function streamTextDeltas(
  response: Response,
  onChunk: (text: string) => void,
  onMeta?: (meta: Record<string, unknown>) => void,
  onWebProgress?: (progress: { source: string; status: string; message: string }) => void
): Promise<void> {
  for await (const { data } of readSSEEvents(response)) {
    if (typeof data !== 'object' || data === null) continue;

    const record = data as Record<string, unknown>;
    if (typeof record.text === 'string') {
      onChunk(record.text);
    }
    if (record.meta && typeof record.meta === 'object' && onMeta) {
      onMeta(record.meta as Record<string, unknown>);
    }
    if (record.webProgress && typeof record.webProgress === 'object' && onWebProgress) {
      onWebProgress(record.webProgress as { source: string; status: string; message: string });
    }
    if (record.webSources && Array.isArray(record.webSources)) {
      // Web sources are attached as a special meta-like event
      onMeta?.({ webSources: record.webSources });
    }
    if (record.quality && typeof record.quality === 'object') {
      onMeta?.({ quality: record.quality });
    }
    if (typeof record.error === 'string') {
      throw new Error(record.error);
    }
    if (record.done === true) return;
  }
}
